import { Avatar, Col, Row, Text } from 'components';
import { format } from 'date-fns';
import { theme } from 'lib';
import React from 'react';
import styled from 'styled-components';

const GameCard = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  border: 1px solid ${theme.GREY_20};
  border-radius: 12px;
  background: #fff;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${theme.GREY_40};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const StatusBadge = styled.div`
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  background: ${(props) => {
    switch (props.status) {
      case 'scheduled':
        return theme.GREY_20;
      case 'in-play':
        return '#4CAF50';
      case 'finished':
        return theme.GREY_40;
      case 'postponed':
        return '#FF9800';
      case 'canceled':
        return '#F44336';
      default:
        return theme.GREY_20;
    }
  }};
  color: ${(props) =>
    ['in-play', 'canceled', 'postponed'].includes(props.status) ? '#fff' : theme.GREY_80};
`;

const ResultBadge = styled.div`
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background: ${(props) => {
    switch (props.result) {
      case 'won':
        return '#4CAF50';
      case 'draw':
        return '#FF9800';
      case 'lost':
        return '#F44336';
      default:
        return theme.GREY_20;
    }
  }};
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const GameItem = ({ game, teamId, onClick }) => {
  const isHome = game.home?._id === teamId;
  const opponent = isHome ? game.away : game.home;
  const teamScore = parseInt(isHome ? game.score?.home : game.score?.away) || 0;
  const opponentScore = parseInt(isHome ? game.score?.away : game.score?.home) || 0;

  const getStatusLabel = (status) => {
    const labels = {
      scheduled: 'Scheduled',
      'in-play': 'Live',
      finished: 'Final',
      postponed: 'Postponed',
      canceled: 'Canceled',
      abandoned: 'Abandoned',
      'not-played': 'Not Played'
    };
    return labels[status] || status;
  };

  const getGameResult = () => {
    if (game.status !== 'finished') return null;
    if (teamScore > opponentScore) return 'won';
    if (teamScore < opponentScore) return 'lost';
    return 'draw';
  };

  const result = getGameResult();

  return (
    <GameCard onClick={() => onClick && onClick(game)}>
      <Col noFlex wid="30%">
        <Text c1 med col={theme.GREY_60}>
          {game.date && format(new Date(game.date), 'MMM dd, yyyy')}
        </Text>
        <Text c2 col={theme.GREY_60}>
          {game.date && format(new Date(game.date), 'h:mm a')}
        </Text>
      </Col>

      <Col noFlex wid="40%" center>
        <Row between centerAll wid="100%">
          <Row center noFlex>
            <Avatar tiny user={opponent} avatar={opponent?.avatar} />
            <Text b2 marg="0 0 0 8px">
              {isHome ? 'vs' : '@'} {opponent?.name || 'TBD'}
            </Text>
          </Row>
        </Row>

        {game.league && (
          <Text c2 col={theme.GREY_60} marg="4px 0 0">
            {game.league}
          </Text>
        )}
      </Col>

      <Col noFlex wid="15%" centerAll>
        {game.status === 'finished' || game.status === 'in-play' ? (
          <Row center gap="8px">
            <Text
              h4
              bold
              col={result === 'won' ? '#4CAF50' : result === 'lost' ? '#F44336' : theme.GREY_80}
            >
              {teamScore}
            </Text>
            <Text h4 col={theme.GREY_60}>
              -
            </Text>
            <Text h4 col={theme.GREY_60}>
              {opponentScore}
            </Text>
          </Row>
        ) : (
          <Text b2 col={theme.GREY_60}>
            -
          </Text>
        )}
      </Col>

      <Col noFlex wid="20%" endAll gap="8px">
        {result && <ResultBadge result={result}>{result}</ResultBadge>}
        {!result && game.status !== 'finished' && (
          <StatusBadge status={game.status}>{getStatusLabel(game.status)}</StatusBadge>
        )}
      </Col>
    </GameCard>
  );
};

export default GameItem;
